import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Music, Palette, Building2, Search, MapPin, Filter, 
  Loader2, Star, Users, ChevronDown, X, Grid, List, Sparkles, Rocket
} from 'lucide-react';


const CATEGORIES = [
  { id: 'all', label: 'All Creatives', icon: Users, color: 'text-secondary' },
  { id: 'producers', label: 'Producers', icon: Music, color: 'text-orange-400' },
  { id: 'designers', label: 'Graphic Designers', icon: Palette, color: 'text-pink-400' },
  { id: 'studios', label: 'Recording Studios', icon: Building2, color: 'text-cyan-400' }
];

const GENRES = ['Hip-Hop', 'R&B', 'Trap', 'Drill', 'Go-Go', 'Pop', 'Soul', 'Gospel', 'Electronic'];
const SPECIALTIES = ['Cover Art', 'Logo Design', 'Motion Graphics', 'Branding', 'Thumbnails', 'Flyers'];
const STUDIO_FEATURES = ['Mixing', 'Mastering', 'Recording', 'Video', 'Podcast', 'Rehearsal'];

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filter states
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  
  // Data states
  const [producers, setProducers] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [studios, setStudios] = useState([]);

  useEffect(() => {
    fetchMarketplaceData();
  }, [category, searchQuery, selectedGenre, selectedSpecialty, selectedCity]);

  const fetchMarketplaceData = async () => {
    setLoading(true);
    
    try {
      const fetchPromises = [];
      
      // Fetch based on category
      if (category === 'all' || category === 'producers') {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedGenre) params.append('genre', selectedGenre);
        if (selectedCity) params.append('city', selectedCity);
        fetchPromises.push(
          fetch(`${API}/discover/producers?${params}`).then(r => r.json()).then(d => setProducers(d.producers || []))
        );
      }
      
      if (category === 'all' || category === 'designers') {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedSpecialty) params.append('specialty', selectedSpecialty);
        if (selectedCity) params.append('city', selectedCity);
        fetchPromises.push(
          fetch(`${API}/discover/designers?${params}`).then(r => r.json()).then(d => setDesigners(d.designers || []))
        );
      }
      
      if (category === 'all' || category === 'studios') {
        const params = new URLSearchParams();
        if (selectedCity) params.append('neighborhood', selectedCity);
        fetchPromises.push(
          fetch(`${API}/studios?${params}`).then(r => r.json()).then(d => setStudios(d || []))
        );
      }
      
      await Promise.all(fetchPromises);
    } catch (err) {
      console.error('Marketplace fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setSearchParams({ category: newCategory, q: searchQuery });
    // Reset category-specific filters
    setSelectedGenre('');
    setSelectedSpecialty('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ category, q: searchQuery });
  };

  const clearFilters = () => {
    setSelectedGenre('');
    setSelectedSpecialty('');
    setSelectedCity('');
    setSearchQuery('');
    setSearchParams({ category });
  };

  const hasActiveFilters = selectedGenre || selectedSpecialty || selectedCity || searchQuery;

  // Combine all results for 'all' view
  const allResults = [
    ...producers.map(p => ({ ...p, type: 'producer' })),
    ...designers.map(d => ({ ...d, type: 'designer' })),
    ...studios.map(s => ({ ...s, type: 'studio' }))
  ];

  const CategoryIcon = CATEGORIES.find(c => c.id === category)?.icon || Users;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="marketplace-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase mb-2">
            Creator <span className="text-secondary">Marketplace</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Find producers for your next track, designers for your visuals, or book a studio session
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={category === cat.id ? 'default' : 'outline'}
                className={category === cat.id ? 'bg-secondary hover:bg-secondary/90' : ''}
                onClick={() => handleCategoryChange(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                <Icon className={`w-4 h-4 mr-2 ${category === cat.id ? '' : cat.color}`} />
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* Search & Filters */}
        <Card className="glass-card border-border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, handle, or specialty..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="marketplace-search"
                />
              </div>
              <Button type="submit">Search</Button>
            </form>

            {/* Filter Toggle */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-muted/50' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-secondary text-xs">Active</Badge>
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap gap-4">
                {/* Genre Filter (Producers) */}
                {(category === 'all' || category === 'producers') && (
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Genre</label>
                    <select
                      value={selectedGenre}
                      onChange={(e) => setSelectedGenre(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">All Genres</option>
                      {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                )}

                {/* Specialty Filter (Designers) */}
                {(category === 'all' || category === 'designers') && (
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Specialty</label>
                    <select
                      value={selectedSpecialty}
                      onChange={(e) => setSelectedSpecialty(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">All Specialties</option>
                      {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {/* City Filter */}
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Location</label>
                  <Input
                    placeholder="City or area..."
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>

                {hasActiveFilters && (
                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" /> Clear All
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {category === 'all' ? (
                  `${allResults.length} creative${allResults.length !== 1 ? 's' : ''} found`
                ) : category === 'producers' ? (
                  `${producers.length} producer${producers.length !== 1 ? 's' : ''} found`
                ) : category === 'designers' ? (
                  `${designers.length} designer${designers.length !== 1 ? 's' : ''} found`
                ) : (
                  `${studios.length} studio${studios.length !== 1 ? 's' : ''} found`
                )}
              </p>
            </div>

            {/* Producers Section */}
            {(category === 'all' || category === 'producers') && producers.length > 0 && (
              <div className="mb-8">
                {category === 'all' && (
                  <div className="flex items-center gap-2 mb-4">
                    <Music className="w-5 h-5 text-orange-400" />
                    <h2 className="font-heading text-xl font-bold">Producers</h2>
                    <Badge variant="outline">{producers.length}</Badge>
                  </div>
                )}
                <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
                  {producers.map((producer) => (
                    <ProducerCard key={producer.user_id} producer={producer} viewMode={viewMode} />
                  ))}
                </div>
              </div>
            )}

            {/* Designers Section */}
            {(category === 'all' || category === 'designers') && designers.length > 0 && (
              <div className="mb-8">
                {category === 'all' && (
                  <div className="flex items-center gap-2 mb-4">
                    <Palette className="w-5 h-5 text-pink-400" />
                    <h2 className="font-heading text-xl font-bold">Graphic Designers</h2>
                    <Badge variant="outline">{designers.length}</Badge>
                  </div>
                )}
                <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
                  {designers.map((designer) => (
                    <DesignerCard key={designer.user_id} designer={designer} viewMode={viewMode} />
                  ))}
                </div>
              </div>
            )}

            {/* Studios Section */}
            {(category === 'all' || category === 'studios') && studios.length > 0 && (
              <div className="mb-8">
                {category === 'all' && (
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-cyan-400" />
                    <h2 className="font-heading text-xl font-bold">Recording Studios</h2>
                    <Badge variant="outline">{studios.length}</Badge>
                  </div>
                )}
                <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
                  {studios.map((studio) => (
                    <StudioCard key={studio.studio_id} studio={studio} viewMode={viewMode} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {((category === 'producers' && producers.length === 0) ||
              (category === 'designers' && designers.length === 0) ||
              (category === 'studios' && studios.length === 0) ||
              (category === 'all' && allResults.length === 0)) && (
              <div className="text-center py-20">
                <CategoryIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No Results Found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || hasActiveFilters 
                    ? 'Try adjusting your search or filters'
                    : 'Be the first to join as a creative!'}
                </p>
                {(searchQuery || hasActiveFilters) && (
                  <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Producer Card Component
function ProducerCard({ producer, viewMode }) {
  // Determine card styling based on boost/featured status
  const getCardStyle = () => {
    if (producer.is_boosted) return 'border-purple-500/50 bg-purple-500/10 ring-1 ring-purple-500/30';
    if (producer.featured) return 'border-yellow-500/40 bg-yellow-500/5';
    return 'border-orange-500/20 hover:border-orange-500/40';
  };

  if (viewMode === 'list') {
    return (
      <Link to={`/producers/${producer.handle}`}>
        <Card className={`glass-card p-4 transition-colors ${getCardStyle()}`}>
          <div className="flex items-center gap-4">
            <div className="relative">
              {producer.picture ? (
                <img src={producer.picture} alt={producer.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Music className="w-8 h-8 text-orange-400" />
                </div>
              )}
              {producer.is_boosted ? (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center animate-pulse">
                  <Rocket className="w-3 h-3 text-white" />
                </div>
              ) : producer.featured && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold">{producer.name}</h3>
                {producer.is_boosted && <Badge className="bg-purple-500/20 text-purple-400 text-xs">Boosted</Badge>}
                {producer.featured && !producer.is_boosted && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Featured</Badge>}
                {producer.is_verified && <Badge className="bg-orange-500/20 text-orange-400 text-xs">Verified</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{producer.handle ? `@${producer.handle}` : ""}</p>
              {producer.profile?.genres?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {producer.profile.genres.slice(0, 3).map((g, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{g}</Badge>
                  ))}
                </div>
              )}
            </div>
            {producer.profile?.location?.city && (
              <div className="text-sm text-muted-foreground flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                {producer.profile.location.city}
              </div>
            )}
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/producers/${producer.handle}`}>
      <Card className={`glass-card p-4 transition-colors h-full ${getCardStyle()}`}>
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            {producer.picture ? (
              <img src={producer.picture} alt={producer.name} className="w-20 h-20 rounded-full object-cover mb-3" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                <Music className="w-10 h-10 text-orange-400" />
              </div>
            )}
            {producer.is_boosted ? (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center animate-pulse">
                <Rocket className="w-4 h-4 text-white" />
              </div>
            ) : producer.featured && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
            )}
          </div>
          {producer.is_boosted ? (
            <Badge className="bg-purple-500/20 text-purple-400 text-xs mb-1">Boosted</Badge>
          ) : producer.featured && (
            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs mb-1">Featured</Badge>
          )}
          <div className="flex items-center gap-1 mb-1">
            <h3 className="font-bold">{producer.name}</h3>
            {producer.is_verified && <Star className="w-4 h-4 text-orange-400 fill-orange-400" />}
          </div>
          <p className="text-sm text-muted-foreground mb-2">{producer.handle ? `@${producer.handle}` : ""}</p>
          {producer.profile?.genres?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1">
              {producer.profile.genres.slice(0, 2).map((g, i) => (
                <Badge key={i} variant="outline" className="text-xs">{g}</Badge>
              ))}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}

// Designer Card Component
function DesignerCard({ designer, viewMode }) {
  // Determine card styling based on boost/featured status
  const getCardStyle = () => {
    if (designer.is_boosted) return 'border-purple-500/50 bg-purple-500/10 ring-1 ring-purple-500/30';
    if (designer.featured) return 'border-yellow-500/40 bg-yellow-500/5';
    return 'border-pink-500/20 hover:border-pink-500/40';
  };

  if (viewMode === 'list') {
    return (
      <Link to={`/designers/${designer.handle}`}>
        <Card className={`glass-card p-4 transition-colors ${getCardStyle()}`}>
          <div className="flex items-center gap-4">
            <div className="relative">
              {designer.picture ? (
                <img src={designer.picture} alt={designer.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Palette className="w-8 h-8 text-pink-400" />
                </div>
              )}
              {designer.is_boosted ? (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center animate-pulse">
                  <Rocket className="w-3 h-3 text-white" />
                </div>
              ) : designer.featured && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold">{designer.name}</h3>
                {designer.is_boosted && <Badge className="bg-purple-500/20 text-purple-400 text-xs">Boosted</Badge>}
                {designer.featured && !designer.is_boosted && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Featured</Badge>}
                {designer.is_verified && <Badge className="bg-pink-500/20 text-pink-400 text-xs">Verified</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{designer.handle ? `@${designer.handle}` : ""}</p>
              {designer.profile?.specialties?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {designer.profile.specialties.slice(0, 3).map((s, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                  ))}
                </div>
              )}
            </div>
            {designer.profile?.location?.city && (
              <div className="text-sm text-muted-foreground flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                {designer.profile.location.city}
              </div>
            )}
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/designers/${designer.handle}`}>
      <Card className={`glass-card p-4 transition-colors h-full ${getCardStyle()}`}>
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            {designer.picture ? (
              <img src={designer.picture} alt={designer.name} className="w-20 h-20 rounded-full object-cover mb-3" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-pink-500/20 flex items-center justify-center mb-3">
                <Palette className="w-10 h-10 text-pink-400" />
              </div>
            )}
            {designer.is_boosted ? (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center animate-pulse">
                <Rocket className="w-4 h-4 text-white" />
              </div>
            ) : designer.featured && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
            )}
          </div>
          {designer.is_boosted ? (
            <Badge className="bg-purple-500/20 text-purple-400 text-xs mb-1">Boosted</Badge>
          ) : designer.featured && (
            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs mb-1">Featured</Badge>
          )}
          <div className="flex items-center gap-1 mb-1">
            <h3 className="font-bold">{designer.name}</h3>
            {designer.is_verified && <Star className="w-4 h-4 text-pink-400 fill-pink-400" />}
          </div>
          <p className="text-sm text-muted-foreground mb-2">{designer.handle ? `@${designer.handle}` : ""}</p>
          {designer.profile?.specialties?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1">
              {designer.profile.specialties.slice(0, 2).map((s, i) => (
                <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
              ))}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}

// Studio Card Component
function StudioCard({ studio, viewMode }) {
  if (viewMode === 'list') {
    return (
      <Link to={`/studios/${studio.studio_id}`}>
        <Card className="glass-card border-cyan-500/20 p-4 hover:border-cyan-500/40 transition-colors">
          <div className="flex items-center gap-4">
            {studio.photos?.[0] ? (
              <img src={studio.photos[0]} alt={studio.name} className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-cyan-400" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold">{studio.name}</h3>
                {studio.is_featured && <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">Featured</Badge>}
              </div>
              {studio.neighborhood && (
                <p className="text-sm text-muted-foreground flex items-center">
                  <MapPin className="w-3 h-3 mr-1" />
                  {studio.neighborhood}
                </p>
              )}
              {studio.services?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {studio.services.slice(0, 3).map((s, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="text-right">
              {studio.hourly_rate && (
                <p className="font-bold text-cyan-400">${studio.hourly_rate}/hr</p>
              )}
              {studio.rating && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Star className="w-3 h-3 mr-1 text-yellow-400 fill-yellow-400" />
                  {studio.rating.toFixed(1)}
                </div>
              )}
            </div>
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/studios/${studio.studio_id}`}>
      <Card className="glass-card border-cyan-500/20 overflow-hidden hover:border-cyan-500/40 transition-colors h-full">
        <div className="aspect-video bg-cyan-500/10 relative">
          {studio.photos?.[0] ? (
            <img src={studio.photos[0]} alt={studio.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-12 h-12 text-cyan-400" />
            </div>
          )}
          {studio.hourly_rate && (
            <Badge className="absolute top-2 right-2 bg-background/70">
              ${studio.hourly_rate}/hr
            </Badge>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold">{studio.name}</h3>
            {studio.is_featured && <Star className="w-4 h-4 text-cyan-400 fill-cyan-400" />}
          </div>
          {studio.neighborhood && (
            <p className="text-sm text-muted-foreground flex items-center mb-2">
              <MapPin className="w-3 h-3 mr-1" />
              {studio.neighborhood}
            </p>
          )}
          {studio.services?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {studio.services.slice(0, 2).map((s, i) => (
                <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
              ))}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}
